import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Home, Sparkles } from "lucide-react";
import { AstronautIcon } from "@/components/icons/icons";

function useHtmlDarkClass(): boolean {
  const [isDark, setIsDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const html = document.documentElement;
    const update = () => setIsDark(html.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(html, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

const STAR_COLORS_DARK = ["#818cf8", "#6366f1", "#38bdf8", "#c084fc", "#a5b4fc", "#e879f9"];
const STAR_COLORS_LIGHT = ["#818cf8", "#6366f1", "#38bdf8", "#c084fc", "#4f46e5", "#c7d2fe"];

const BURST_COLORS = [
  "var(--not-found-burst-1)",
  "var(--not-found-burst-2)",
  "var(--not-found-burst-3)",
  "var(--not-found-burst-4)",
  "var(--not-found-burst-5)",
];

const digitBaseStyle: React.CSSProperties = {
  fontFamily: "'Arial Nova', 'Arial Black', Arial, sans-serif",
  fontWeight: 900,
  lineHeight: 1,
  letterSpacing: "-8px",
  display: "inline-block",
  userSelect: "none",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  transformStyle: "preserve-3d",
};

// ─── Click burst ───────────────────────────────────────────────────────────────
interface BurstParticle {
  id: number;
  x: number;
  y: number;
  particles: Array<{ angle: number; dist: number; color: string }>;
}

function ClickBurst({ burst, onDone }: { burst: BurstParticle; onDone: () => void }) {
  return (
    <div className="pointer-events-none absolute" style={{ left: burst.x, top: burst.y }}>
      {burst.particles.map((p, j) => (
        <div
          key={j}
          className="absolute h-2 w-2 rounded-full"
          style={{
            background: p.color,
            left: -4,
            top: -4,
            animation: "burst-out 0.7s ease-out forwards",
            animationDelay: `${j * 20}ms`,
            ["--burst-x" as string]: `${Math.cos((p.angle * Math.PI) / 180) * p.dist}px`,
            ["--burst-y" as string]: `${Math.sin((p.angle * Math.PI) / 180) * p.dist}px`,
          }}
          onAnimationEnd={j === burst.particles.length - 1 ? onDone : undefined}
        />
      ))}
    </div>
  );
}

// ─── 404 digit — solid mode-aware color, drop-in animation
function GlowDigit({ char, delay }: { char: string; delay: number }) {
  return (
    <div className="relative" style={{ marginRight: 4, zIndex: 10 }}>
      {/* Blurred glow shadow — same mode-aware color, offset + blur */}
      <span
        aria-hidden
        className="pointer-events-none absolute text-[clamp(80px,18vw,200px)] blur-[16px] opacity-20"
        style={{
          ...digitBaseStyle,
          backgroundImage: "linear-gradient(90deg, var(--not-found-digit-main) 0%, var(--not-found-digit-main) 100%)",
          top: 6,
          left: 2,
          animation: `digit-drop-in 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms both`,
        }}
      >
        {char}
      </span>
      {/* Main digit */}
      <span
        className="text-[clamp(80px,18vw,200px)]"
        style={{
          ...digitBaseStyle,
          backgroundImage: "linear-gradient(90deg, var(--not-found-digit-main) 0%, var(--not-found-digit-main) 100%)",
          filter: "drop-shadow(0px 0px 30px var(--not-found-digit-shadow))",
          animation: `digit-drop-in 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms both`,
        }}
      >
        {char}
      </span>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function NotFoundPage() {
  const navigate = useNavigate();
  const isDark = useHtmlDarkClass();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDarkRef = useRef(isDark);
  isDarkRef.current = isDark;

  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [bursts, setBursts] = useState<BurstParticle[]>([]);
  const burstIdRef = useRef(0);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }, []);

  const handleClick = useCallback((e: MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = ++burstIdRef.current;
    setBursts(prev => [
      ...prev.slice(-5),
      {
        id,
        x,
        y,
        particles: Array.from({ length: 10 }, (_, j) => ({
          angle: (j / 10) * 360,
          dist: Math.random() * 60 + 30,
          color: BURST_COLORS[j % BURST_COLORS.length],
        })),
      },
    ]);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("mousemove", handleMouseMove);
    el.addEventListener("click", handleClick);
    return () => {
      el.removeEventListener("mousemove", handleMouseMove);
      el.removeEventListener("click", handleClick);
    };
  }, [handleMouseMove, handleClick]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    interface Star {
      x: number; y: number;
      radius: number; baseOpacity: number;
      speed: number; twinkleOffset: number;
      color: string; blur: boolean;
      dx: number; dy: number;
      fadePhase: number; fadeSpeed: number;
    }
    interface Shooting {
      x: number; y: number; vx: number;
      length: number; thickness: number;
      life: number; maxLife: number;
    }

    let stars: Star[] = [];
    let shooters: Shooting[] = [];
    let time = 0;
    let nextShoot = 60;
    let rafId = 0;

    const buildStars = (w: number, h: number) => {
      const cols = isDarkRef.current ? STAR_COLORS_DARK : STAR_COLORS_LIGHT;
      const count = Math.min(320, Math.round((w * h) / 4000));
      stars = Array.from({ length: count }, () => {
        const big = Math.random() < 0.2;
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          radius: big ? Math.random() * 3 + 2.5 : Math.random() * 1.4 + 0.4,
          baseOpacity: big ? Math.random() * 0.55 + 0.4 : Math.random() * 0.5 + 0.2,
          speed: Math.random() * 0.7 + 0.2,
          twinkleOffset: Math.random() * Math.PI * 2,
          color: cols[Math.floor(Math.random() * cols.length)],
          blur: big,
          dx: (Math.random() - 0.5) * 0.35,
          dy: (Math.random() - 0.5) * 0.2,
          fadePhase: Math.random() * Math.PI * 2,
          fadeSpeed: Math.random() * 0.008 + 0.004,
        };
      });
    };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      buildStars(canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);

    const spawnShooting = (w: number, h: number) => {
      const length = Math.random() * 160 + 80;
      const speed = Math.random() * 12 + 12;
      shooters.push({
        x: -length,
        y: Math.random() * h * 0.65,
        vx: speed,
        length,
        thickness: Math.random() * 1.5 + 1,
        life: Math.round((w + length) / speed),
        maxLife: Math.round((w + length) / speed),
      });
    };

    const draw = () => {
      time += 0.016;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      nextShoot -= 1;
      if (nextShoot <= 0) {
        spawnShooting(w, h);
        nextShoot = Math.round(Math.random() * 180 + 120);
      }
      shooters = shooters.filter((ss) => {
        ss.x += ss.vx;
        ss.life -= 1;
        const t = ss.life / ss.maxLife;
        const grad = ctx.createLinearGradient(ss.x, ss.y, ss.x + ss.length, ss.y);
        grad.addColorStop(0, "rgba(0,0,0,0)");
        grad.addColorStop(0.5, `rgba(167,139,250,${(t * 0.98).toFixed(3)})`);
        grad.addColorStop(1, `rgba(255,255,255,${(t * 0.98).toFixed(3)})`);
        ctx.globalAlpha = 1;
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(ss.x, ss.y - ss.thickness, ss.length, ss.thickness * 2, ss.thickness);
        ctx.fill();
        return ss.life > 0 && ss.x < w + ss.length;
      });

      for (const s of stars) {
        s.x += s.dx;
        s.y += s.dy;
        s.fadePhase += s.fadeSpeed;
        const fadeMult = Math.sin(s.fadePhase) * 0.5 + 0.5;

        if (fadeMult < 0.02 && s.fadePhase > Math.PI) {
          s.x = Math.random() * w;
          s.y = Math.random() * h;
          s.dx = (Math.random() - 0.5) * 0.35;
          s.dy = (Math.random() - 0.5) * 0.2;
          s.fadePhase = 0;
        }

        if (s.x < -10) s.x = w + 10;
        if (s.x > w + 10) s.x = -10;
        if (s.y < -10) s.y = h + 10;
        if (s.y > h + 10) s.y = -10;

        const twinkle = Math.sin(time * s.speed * 3 + s.twinkleOffset);
        const opacity = s.baseOpacity * fadeMult * (0.4 + 0.6 * (twinkle * 0.5 + 0.5));
        if (opacity <= 0.01) continue;

        const radius = s.radius * (0.6 + 0.4 * (twinkle * 0.5 + 0.5));

        if (s.blur) {
          ctx.globalAlpha = Math.max(0, opacity * 0.18);
          ctx.fillStyle = s.color;
          ctx.beginPath();
          ctx.arc(s.x, s.y, radius * 3, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.globalAlpha = Math.max(0, opacity);
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
        ctx.fill();

        if (s.blur && twinkle > 0.6) {
          const arm = radius * 3;
          ctx.globalAlpha = opacity * 0.5;
          ctx.strokeStyle = s.color;
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.moveTo(s.x - arm, s.y); ctx.lineTo(s.x + arm, s.y);
          ctx.moveTo(s.x, s.y - arm); ctx.lineTo(s.x, s.y + arm);
          ctx.stroke();
        }
      }

      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes burst-out {
          0%   { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(var(--burst-x), var(--burst-y)) scale(0); opacity: 0; }
        }
      `}</style>

      <div
        ref={containerRef}
        className="fixed inset-0 overflow-hidden cursor-crosshair"
        style={{ zIndex: 9999 }}
      >
        {/* Background */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-not-found-bg" />
          <div className="bg-glow-indigo absolute -left-[138px] -top-[228px] h-[882px] w-[882px] rounded-full opacity-20 blur-[64px]" />
          <div className="bg-glow-sky absolute -right-[230px] top-[175px] h-[735px] w-[735px] rounded-full opacity-[0.15] blur-[64px]" />
          <div className="bg-glow-fuchsia absolute left-[calc(50%-50px)] top-[30%] h-[588px] w-[588px] rounded-full opacity-10 blur-[64px]" />
          <canvas ref={canvasRef} className="absolute inset-0 h-full w-full opacity-70" aria-hidden />
        </div>

        {/* Click bursts */}
        {bursts.map(burst => (
          <ClickBurst
            key={burst.id}
            burst={burst}
            onDone={() => setBursts(prev => prev.filter(b => b.id !== burst.id))}
          />
        ))}

        {/* Main content — fits viewport height without scroll */}
        <div className="relative z-10 flex h-full flex-col items-center justify-center gap-0 px-4">

          {/* 404 */}
          <div
            className="pointer-events-none relative z-20 flex select-none items-end justify-center"
            style={{ perspective: "800px", transformStyle: "preserve-3d" }}
          >
            <GlowDigit char="4" delay={0} />
            <GlowDigit char="0" delay={120} />
            <GlowDigit char="4" delay={240} />
          </div>

          {/* Orbital rings + astronaut */}
          <div
            className="relative flex items-center justify-center"
            style={{ width: "min(540px, 90vw)", height: "min(240px, 30vh)", overflow: "visible", marginTop: "-0.5rem" }}
          >
            {/* Ring 1 */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: "min(300px, 55vw)", height: "min(300px, 55vw)" }}>
              <div className="animate-orbit-cw-slow relative h-full w-full rounded-full border opacity-25" style={{ borderColor: "var(--not-found-burst-1)" }}>
                <div className="absolute rounded-full" style={{ width: 8, height: 8, top: -4, left: "calc(50% - 4px)", background: "var(--not-found-burst-1)", boxShadow: "0 0 6px 2px var(--not-found-burst-1)" }} />
              </div>
            </div>

            {/* Ring 2 */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: "min(400px, 72vw)", height: "min(400px, 72vw)" }}>
              <div className="animate-orbit-ccw-slow relative h-full w-full rounded-full border opacity-25" style={{ borderColor: "var(--not-found-burst-2)" }}>
                <div className="absolute rounded-full" style={{ width: 8, height: 8, bottom: -4, left: "calc(50% - 4px)", background: "var(--not-found-burst-2)", boxShadow: "0 0 6px 2px var(--not-found-burst-2)" }} />
              </div>
            </div>

            {/* Ring 3 */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: "min(490px, 88vw)", height: "min(490px, 88vw)" }}>
              <div className="animate-orbit-cw-slower relative h-full w-full rounded-full border opacity-25" style={{ borderColor: "var(--not-found-burst-3)" }}>
                <div className="absolute rounded-full" style={{ width: 8, height: 8, top: "calc(50% - 4px)", right: -4, background: "var(--not-found-burst-3)", boxShadow: "0 0 6px 2px var(--not-found-burst-3)" }} />
              </div>
            </div>

            {/* Astronaut — outer div handles body float+rotate, inner has tether/thruster/icon */}
            <div className="animate-astronaut-body relative z-10 flex-shrink-0" style={{ width: 130, height: 178 }}>
              {/* Radial glow halo */}
              <div
                className="animate-glow-halo pointer-events-none absolute rounded-full blur-3xl"
                style={{
                  inset: 0,
                  background: "radial-gradient(circle, #818cf8 0%, #a78bfa 40%, transparent 70%)",
                  transform: "scale(1.8)",
                  opacity: 0.3,
                }}
              />
              {/* Tether */}
              <div
                className="animate-tether-sway absolute origin-top"
                style={{ left: "calc(50% - 0.5px)", top: -64, width: 1, height: 64, background: "linear-gradient(to bottom, var(--color-tether-start), var(--color-tether-end))" }}
              />
              {/* Thruster */}
              <div
                className="animate-thruster absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full blur-md"
                style={{ width: 26, height: 32, background: "linear-gradient(to bottom, var(--not-found-burst-1), var(--not-found-burst-3), transparent)" }}
              />
              <AstronautIcon mouseX={mousePos.x} mouseY={mousePos.y} className="h-full w-full object-contain relative z-10" />
            </div>
          </div>

          {/* Text block */}
          <div className="mt-1 flex max-w-[580px] flex-col items-center px-4 text-center">
            <h1 className="mb-2 text-[22px] font-bold leading-[1.1] tracking-[-0.234px] text-not-found-heading sm:text-[32px]">
              Lost in the career cosmos
            </h1>
            <p className="max-w-[500px] text-[13px] font-normal leading-[1.7] tracking-[-0.313px] text-not-found-body sm:text-[16px]">
              Our astronaut searched every orbit of AchieveOS but couldn&apos;t find this page.
              It may have drifted into deep space, or the URL might be out of alignment.
            </p>

            {/* Hint pill */}
            <div
              className="animate-pill-pulse mt-3 inline-flex h-[22px] items-center gap-1.5 rounded-full border border-not-found-pill-border bg-not-found-pill-bg px-3"
              style={{ backdropFilter: "blur(10px)" }}
            >
              <Sparkles className="h-2 w-2 shrink-0 text-not-found-pill-icon" />
              <span className="text-[10px] font-normal leading-none tracking-[-0.086px] text-not-found-pill-text">
                Click anywhere to fire thrusters!
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate("/home"); }}
              className="group relative flex h-[46px] w-[247px] items-center justify-center gap-2 overflow-hidden rounded-[14px] text-[14px] font-semibold leading-5 tracking-[-0.15px] text-white shadow-not-found-cta transition-opacity hover:opacity-95"
              style={{
                backgroundImage: "linear-gradient(135deg, var(--color-primary-750) 0%, var(--color-primary-purple) 100%)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <span
                className="animate-shimmer pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
                style={{ background: "linear-gradient(105deg, transparent 30%, var(--not-found-btn-shimmer) 50%, transparent 70%)" }}
                aria-hidden
              />
              <Home className="relative z-10 h-4 w-4" />
              <span className="relative z-10">Return to Home Page</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
